import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import { unifiedApiService } from '../../services/unified';
import { useGenerateSocialPost } from '../../hooks/unified/useAIQueries';
import type { Job } from '../../types/job';
import type { EnhancedJobStatus } from '../../services/unified/types';

interface JobWithAnalytics extends Job {
  views?: number;
  applications?: number;
  conversionRate?: number;
  status?: EnhancedJobStatus;
  is_featured?: boolean;
  created_at?: string; // Backend returns created_at
}

interface BulkActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedJobs: string[];
  onActionComplete: () => void;
}

function BulkActionModal({ isOpen, onClose, selectedJobs, onActionComplete }: BulkActionModalProps) {
  const [action, setAction] = useState<'publish' | 'archive' | 'delete'>('publish');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (action === 'delete') {
        await unifiedApiService.jobs.bulkDeleteJobs({ job_ids: selectedJobs });
      } else {
        await unifiedApiService.jobs.bulkUpdateJobStatus({ job_ids: selectedJobs, status: action });
      }
      onActionComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
      <div className="relative top-10 sm:top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Bulk Action</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            You have selected {selectedJobs.length} job(s). What would you like to do?
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="publish"
                    checked={action === 'publish'}
                    onChange={(e) => setAction(e.target.value as 'publish')}
                    className="mr-2"
                  />
                  <span className="text-sm">Publish jobs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="archive"
                    checked={action === 'archive'}
                    onChange={(e) => setAction(e.target.value as 'archive')}
                    className="mr-2"
                  />
                  <span className="text-sm">Archive jobs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="delete"
                    checked={action === 'delete'}
                    onChange={(e) => setAction(e.target.value as 'delete')}
                    className="mr-2"
                  />
                  <span className="text-sm text-red-600">Delete jobs permanently</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                  action === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {loading ? 'Processing...' : `${action === 'delete' ? 'Delete' : action === 'publish' ? 'Publish' : 'Archive'} Jobs`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function AdminJobsPage() {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [jobs, setJobs] = useState<JobWithAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | EnhancedJobStatus>('all');
  const [showSocialPostModal, setShowSocialPostModal] = useState(false);
  const [selectedJobForSocial, setSelectedJobForSocial] = useState<string | null>(null);
  
  const generateSocialPost = useGenerateSocialPost();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        // Use admin endpoint to get all jobs including drafts
        const response = await unifiedApiService.jobs.getAdminJobsList('all');
        const jobsData = response.jobs;
        
        // Map jobs to include analytics data
        const jobsWithAnalytics = jobsData.map((job: any) => ({
          ...job,
          views: job.viewCount || 0,
          applications: job.applicationCount || 0,
          conversionRate: job.applicationCount && job.viewCount 
            ? Math.round((job.applicationCount / job.viewCount) * 100 * 100) / 100
            : 0,
          status: (job.status || 'draft') as EnhancedJobStatus,
          is_featured: job.featured || job.is_featured || false,
        }));
        
        setJobs(jobsWithAnalytics);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleSelectJob = (jobId: string) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleSelectAll = () => {
    const filteredJobs = getFilteredJobs();
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(job => job.id));
    }
  };

  const handleBulkActionComplete = () => {
    setSelectedJobs([]);
    // Refresh jobs list
    window.location.reload();
  };

  const handleCreateJob = () => {
    navigate('/admin/jobs/create?mode=builder');
  };

  const handleEditJob = (jobId: string) => {
    navigate(`/admin/jobs/create?mode=builder&jobId=${jobId}`);
  };

  const handleStatusChange = async (jobId: string, newStatus: EnhancedJobStatus) => {
    try {
      await unifiedApiService.jobs.updateJobStatus(jobId, newStatus, `Status changed to ${newStatus}`);
      // Refresh jobs list
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? { ...job, status: newStatus } : job
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update job status');
    }
  };

  const handleTogglePin = (jobId: string) => {
    // Toggle pin status locally (stored in localStorage)
    const pinnedJobs = JSON.parse(localStorage.getItem('pinnedJobs') || '[]');
    const isPinned = pinnedJobs.includes(jobId);
    
    if (isPinned) {
      const updated = pinnedJobs.filter((id: string) => id !== jobId);
      localStorage.setItem('pinnedJobs', JSON.stringify(updated));
    } else {
      pinnedJobs.push(jobId);
      localStorage.setItem('pinnedJobs', JSON.stringify(pinnedJobs));
    }
    
    // Force re-render
    setJobs([...jobs]);
  };

  const isPinned = (jobId: string): boolean => {
    const pinnedJobs = JSON.parse(localStorage.getItem('pinnedJobs') || '[]');
    return pinnedJobs.includes(jobId);
  };

  const handleCreateSocialPost = (jobId: string) => {
    setSelectedJobForSocial(jobId);
    setShowSocialPostModal(true);
  };

  const getFilteredJobs = () => {
    const filtered = jobs.filter(job => {
      const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (job.company && job.company.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
    
    // Sort pinned jobs to the top
    const pinnedJobs = JSON.parse(localStorage.getItem('pinnedJobs') || '[]');
    return filtered.sort((a, b) => {
      const aIsPinned = pinnedJobs.includes(a.id);
      const bIsPinned = pinnedJobs.includes(b.id);
      
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      return 0;
    });
  };

  const filteredJobs = getFilteredJobs();

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Job Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Create, edit, and manage job postings with analytics.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {selectedJobs.length > 0 && (
              <button
                onClick={() => setShowBulkModal(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium"
              >
                Bulk ({selectedJobs.length})
              </button>
            )}
            <button
              onClick={handleCreateJob}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium"
            >
              Create Job
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search jobs by title or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                onChange={handleSelectAll}
                className="mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({filteredJobs.length} jobs)
              </span>
            </div>
          </div>
          
          <ul className="divide-y divide-gray-200">
            {filteredJobs.map((job) => (
              <li key={job.id}>
                {/* Mobile Layout */}
                {isMobile ? (
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(job.id)}
                        onChange={() => handleSelectJob(job.id)}
                        className="mt-1 min-w-[20px] min-h-[20px]"
                      />
                      
                      {job.companyLogo && (
                        <img
                          src={job.companyLogo}
                          alt={job.company || 'Company logo'}
                          className="h-10 w-10 rounded-md object-cover border border-gray-200 flex-shrink-0"
                        />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {/* Title and badges */}
                        <div className="flex flex-wrap items-center gap-1 mb-1">
                          <h3 className="text-base font-medium text-gray-900 truncate max-w-[200px]">
                            {job.title}
                          </h3>
                          {isPinned(job.id) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                              📌
                            </span>
                          )}
                          {job.is_featured && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              ⭐
                            </span>
                          )}
                        </div>
                        
                        {/* Status badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${
                          job.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : job.status === 'draft'
                            ? 'bg-gray-100 text-gray-800'
                            : job.status === 'archived'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : 'Draft'}
                        </span>
                        
                        {/* Meta info */}
                        <div className="text-xs text-gray-500 mb-2">
                          {job.company && <span>{job.company}</span>}
                          {job.location && <span> • {job.location}</span>}
                        </div>
                        
                        {/* Analytics row */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                          <span><strong>{job.views || 0}</strong> views</span>
                          <span><strong>{job.applications || 0}</strong> apps</span>
                          <span><strong>{job.conversionRate || 0}%</strong> conv</span>
                        </div>
                        
                        {/* Actions row */}
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={job.status || 'draft'}
                            onChange={(e) => handleStatusChange(job.id, e.target.value as EnhancedJobStatus)}
                            className="text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[36px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                            <option value="closed">Closed</option>
                          </select>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(job.id);
                            }}
                            className={`p-2 rounded-md min-w-[36px] min-h-[36px] flex items-center justify-center ${
                              isPinned(job.id)
                                ? 'text-primary-600 bg-primary-50' 
                                : 'text-gray-400 bg-gray-50'
                            }`}
                          >
                            <svg className="w-4 h-4" fill={isPinned(job.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                          </button>
                          
                          <Link
                            to={`/admin/jobs/${job.id}`}
                            className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md min-h-[36px] flex items-center"
                          >
                            Analytics
                          </Link>
                          
                          <button
                            onClick={() => handleEditJob(job.id)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-md min-h-[36px]"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Desktop Layout */
                  <div className="px-4 py-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(job.id)}
                        onChange={() => handleSelectJob(job.id)}
                        className="mr-4"
                      />
                      
                      {job.companyLogo && (
                        <div className="flex-shrink-0 mr-4">
                          <img
                            src={job.companyLogo}
                            alt={job.company || 'Company logo'}
                            className="h-12 w-12 rounded-md object-cover border border-gray-200"
                          />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-gray-900 truncate">
                                {job.title}
                              </h3>
                              {isPinned(job.id) && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                                  </svg>
                                  Pinned
                                </span>
                              )}
                              {job.is_featured && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  Featured
                                </span>
                              )}
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                job.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : job.status === 'draft'
                                  ? 'bg-gray-100 text-gray-800'
                                  : job.status === 'archived'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : 'Draft'}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              {job.company && (
                                <>
                                  <span>{job.company}</span>
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              {job.location && (
                                <>
                                  <span>{job.location}</span>
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              {job.type && (
                                <>
                                  <span className="capitalize">{job.type.replace('-', ' ')}</span>
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              {job.experience && (
                                <>
                                  <span className="capitalize">{job.experience}</span>
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              <span>Posted {(job.created_at || job.postedDate) ? new Date(job.created_at || job.postedDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                          </div>
                          
                          {/* Analytics */}
                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <div className="text-center">
                              <div className="font-medium text-gray-900">{job.views || 0}</div>
                              <div>Views</div>
                            </div>
                            <div className="text-center">
                              <div className="font-medium text-gray-900">{job.applications || 0}</div>
                              <div>Applications</div>
                            </div>
                            <div className="text-center">
                              <div className="font-medium text-gray-900">{job.conversionRate || 0}%</div>
                              <div>Conversion</div>
                            </div>
                          </div>
                          
                          {/* Status Management */}
                          <div className="flex items-center space-x-3 ml-6">
                            <select
                              value={job.status || 'draft'}
                              onChange={(e) => handleStatusChange(job.id, e.target.value as EnhancedJobStatus)}
                              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="draft">Draft</option>
                              <option value="active">Active</option>
                              <option value="archived">Archived</option>
                              <option value="closed">Closed</option>
                            </select>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePin(job.id);
                              }}
                              className={`text-sm font-medium ${
                                isPinned(job.id)
                                  ? 'text-primary-600 hover:text-primary-800' 
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={isPinned(job.id) ? 'Unpin job' : 'Pin job to top'}
                            >
                              <svg className="w-5 h-5" fill={isPinned(job.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            <Link
                              to={`/admin/jobs/${job.id}`}
                              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                            >
                              Analytics
                            </Link>
                            <button
                              onClick={() => handleEditJob(job.id)}
                              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <Link
                              to={`/jobs/${job.id}`}
                              target="_blank"
                              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                            >
                              Preview
                            </Link>
                            <button
                              onClick={() => handleCreateSocialPost(job.id)}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                            >
                              Social Post
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          
          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first job posting.'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <div className="mt-6">
                  <button
                    onClick={handleCreateJob}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Create New Job
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <BulkActionModal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          selectedJobs={selectedJobs}
          onActionComplete={handleBulkActionComplete}
        />

        <SocialPostModal
          isOpen={showSocialPostModal}
          onClose={() => {
            setShowSocialPostModal(false);
            setSelectedJobForSocial(null);
          }}
          jobId={selectedJobForSocial}
          generateSocialPost={generateSocialPost}
        />
      </div>
    </AdminLayout>
  );
}

interface SocialPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
  generateSocialPost: ReturnType<typeof useGenerateSocialPost>;
}

function SocialPostModal({ isOpen, onClose, jobId, generateSocialPost }: SocialPostModalProps) {
  const [platform, setPlatform] = useState<'instagram_square' | 'instagram_story' | 'linkedin_post' | 'twitter_post' | 'facebook_post'>('instagram_square');
  const [style, setStyle] = useState<'professional' | 'modern' | 'creative' | 'minimalist'>('professional');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!jobId) return;
    
    try {
      const result = await generateSocialPost.mutateAsync({
        job_id: jobId,
        platform,
        style,
      });
      setGeneratedImage(result.image_url);
    } catch (err) {
      console.error('Failed to generate social post:', err);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `job-post-${platform}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
      <div className="relative top-4 sm:top-20 mx-auto p-5 border w-full max-w-[600px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Generate Social Media Post</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as typeof platform)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="instagram_square">Instagram Square (1080x1080)</option>
                <option value="instagram_story">Instagram Story (1080x1920)</option>
                <option value="linkedin_post">LinkedIn Post (1200x627)</option>
                <option value="twitter_post">Twitter Post (1200x675)</option>
                <option value="facebook_post">Facebook Post (1200x630)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as typeof style)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="professional">Professional</option>
                <option value="modern">Modern</option>
                <option value="creative">Creative</option>
                <option value="minimalist">Minimalist</option>
              </select>
            </div>

            {generatedImage && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <img
                  src={generatedImage}
                  alt="Generated social post"
                  className="w-full rounded-md border border-gray-300"
                />
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              {generatedImage && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                >
                  Download
                </button>
              )}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generateSocialPost.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
              >
                {generateSocialPost.isPending ? 'Generating...' : generatedImage ? 'Regenerate' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}